import React from 'react'
import { Stack, Box, Typography, Divider, alpha, useTheme, Button } from '@mui/material'

import EvStationIcon from '@mui/icons-material/EvStation'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import PaymentIcon from '@mui/icons-material/Payment'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import BoltIcon from '@mui/icons-material/Bolt'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import { numericUtils } from '@/utils/numeric'
import { ChargingSessionInAppNotificationStatus } from '@/lib/in-app-notifications/categories/charging_session'
import { ChargingHistory } from '@/types/Charging'
import { HistoryValueBox } from './HistoryValueBox'
import { timeUtils } from '@/utils/time'
import { chargingUtils } from '@/utils/charging'

type HistoryItemConfig = {
  label: string
  color: 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary'
  icon: React.ElementType
}

export const STATUS_CONFIG: Record<ChargingSessionInAppNotificationStatus, HistoryItemConfig> = {
  [ChargingSessionInAppNotificationStatus.NOT_STARTED]: {
    label: 'Aguardando',
    color: 'secondary',
    icon: AccessTimeIcon,
  },
  [ChargingSessionInAppNotificationStatus.READY_TO_START]: {
    label: 'Autorizado',
    color: 'success',
    icon: CheckCircleOutlineIcon,
  },
  [ChargingSessionInAppNotificationStatus.WAITING_PAYMENT]: {
    label: 'Aguardando Pagamento',
    color: 'warning',
    icon: PaymentIcon,
  },
  [ChargingSessionInAppNotificationStatus.FAILED_PAYMENT]: {
    label: 'Falha no Pagamento',
    color: 'error',
    icon: ErrorOutlineIcon,
  },
  [ChargingSessionInAppNotificationStatus.IN_PROGRESS]: {
    label: 'Em andamento',
    color: 'secondary',
    icon: EvStationIcon,
  },
  [ChargingSessionInAppNotificationStatus.FAILED]: { label: 'Falha', color: 'error', icon: ErrorOutlineIcon },
  [ChargingSessionInAppNotificationStatus.FINISHING]: { label: 'Finalizando', color: 'info', icon: AutorenewIcon },
  [ChargingSessionInAppNotificationStatus.IDLE]: { label: 'Ocioso', color: 'warning', icon: PauseCircleOutlineIcon },
  [ChargingSessionInAppNotificationStatus.FINISHED]: {
    label: 'Concluído',
    color: 'success',
    icon: CheckCircleOutlineIcon,
  },
}

export interface HistoryItemProps {
  historyItem: ChargingHistory
  onClickDetails?: (historyItem: ChargingHistory) => void
}

export const HistoryItem = ({ historyItem, onClickDetails }: HistoryItemProps) => {
  const theme = useTheme()

  const chargeStartTime = chargingUtils.getChargeStartTime(historyItem)
  const chargeEndTime = chargingUtils.getChargeEndTime(historyItem)

  const duration = timeUtils.calculateDuration(chargeStartTime, chargeEndTime)
  const formattedStartDate = timeUtils.formatDateWithTime(chargeStartTime, '--')

  const itemConfig = STATUS_CONFIG[historyItem.status] || {
    label: 'Desconhecido',
    color: 'error',
    icon: ErrorOutlineIcon,
  }

  const StatusIcon = itemConfig.icon

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
        width: '100%',
        minHeight: 160,
        flexShrink: 0,
        boxShadow: theme.shadows[1],
      }}
    >
      <Box bgcolor={`${itemConfig.color}.main`} py={0.5} textAlign="center">
        <Typography color="white" variant="subtitle2" fontWeight="bold">
          {itemConfig.label}
        </Typography>
      </Box>

      <Box sx={{ p: 3, paddingBottom: 1.5 }}>
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: alpha(theme.palette[itemConfig.color].main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <StatusIcon color={itemConfig.color} sx={{ fontSize: 24 }} />
          </Box>
          <Stack spacing={0.5} overflow="hidden">
            <Typography variant="subtitle1" fontWeight="bold" noWrap>
              {historyItem.station_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formattedStartDate}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} mb={2}>
          <HistoryValueBox icon={<AccessTimeIcon fontSize="small" />} label="Duração" value={duration} />
          <HistoryValueBox
            icon={<BoltIcon fontSize="small" />}
            label="Consumo"
            value={`${numericUtils.formatKwh(historyItem.kwh_delivered)} kWh`}
          />
          <HistoryValueBox
            icon={<CreditCardIcon fontSize="small" />}
            label="Total"
            value={numericUtils.formatNumberToBRLCurrency(historyItem.total_cost)}
          />
        </Stack>

        <Divider sx={{ mb: 1 }} />

        <Button fullWidth onClick={() => onClickDetails?.(historyItem)} sx={{ color: 'text.secondary' }}>
          Ver detalhes
        </Button>
      </Box>
    </Box>
  )
}
